import { t } from "i18next";
import { toast } from "@/utils/toast";

/**
 * The impersonation write flag and its choke point, in a module of their own.
 *
 * Deliberately not in `useImpersonationGuard`: that module imports
 * `AuthContext` for its rendering hook, and `AuthContext` has to set the flag,
 * which closes an import cycle. Such a cycle resolves only while the setter is
 * a hoisted declaration and `useAuth` is referenced lazily — the kind of thing
 * that survives until someone adds a top-level evaluation and then fails at
 * init, in bundle order. One tiny module both sides import
 * removes the cycle instead of relying on that.
 */

/** Marks the error `assertWritable` throws, for a caller that wants to tell a
 *  refusal from a real failure. Not a display string — the message is
 *  translated at throw time, because a few call sites surface `error.message`
 *  directly (`routes/admin/administrators.lazy.tsx`). */
export const IMPERSONATION_WRITE_REFUSED = "IMPERSONATION_WRITE_REFUSED";

export class ImpersonationWriteRefusedError extends Error {
  readonly code = IMPERSONATION_WRITE_REFUSED;

  constructor(message: string) {
    super(message);
    this.name = "ImpersonationWriteRefusedError";
  }
}

let impersonationActive = false;

/** Called by `AuthContext` whenever the impersonation claim is evaluated. */
export function setImpersonationActive(active: boolean): void {
  impersonationActive = active;
}

export function isImpersonationActive(): boolean {
  return impersonationActive;
}

/**
 * The write choke point: every mutation hook calls this first, so a surface
 * that forgets to disable its control still cannot produce a misattributed
 * document.
 *
 * It also *says* why, before throwing. Thirty-one mutation hooks are guarded
 * and only some of their surfaces disable the control, so an unguarded button
 * would otherwise land in a generic "could not save — try again" catch: a
 * retry prompt for something that can never succeed. Toasting
 * here means one place makes every mutation degrade to an accurate message,
 * including mutations written later. Where the control *is* disabled this is
 * unreachable, which is the better experience and stays the goal.
 */
export function assertWritable(): void {
  if (!impersonationActive) return;

  const message = t("common:impersonation.writeRefused", {
    defaultValue:
      "Not available while you are viewing the app as another user. Stop impersonating to continue.",
  });
  toast.warning(message);
  throw new ImpersonationWriteRefusedError(message);
}
